/* Atlas v3 — Pick of the Week writeup sheet (mobile).
   Mirrors the desktop V2 PickDetail "WeeklyArticle" (thesis + analysis_md +
   price strip + 8/8 gate + signal chips + check-ins), V3-styled as a bottom
   sheet. Reads the `active` pick object the Overview already fetches
   (usePickOfWeek) — no extra endpoint. Tapping the ticker opens its chart. */
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { StockLogo } from '@/v2/components/StockLogo'
import { intent, REDUCE } from './lib'

function fmtPrice(v) { return v == null ? '—' : `$${Number(v).toFixed(2)}` }
function fmtPct(v) { return v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%` }

function PriceCell({ label, value, sub, subColor, divider }) {
  return (
    <div style={{ flex: 1, padding: '11px 13px', borderLeft: divider ? '1px solid hsl(var(--studio-border) / 0.6)' : 'none' }}>
      <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.16em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 5 }}>{label}</div>
      <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 15, color: 'hsl(var(--studio-text))', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, marginTop: 3, color: subColor || 'hsl(var(--studio-text-subtle))' }}>{sub}</div>}
    </div>
  )
}

// Render a writeup paragraph: **bold** spans → <strong>, the rest plain. The
// pick writer emits sub-points as "**Lead-in.** body", so we render the lead-in
// bold instead of leaking literal asterisks into the prose.
function renderInline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((seg, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(seg)
    return m
      ? <strong key={i} style={{ color: 'hsl(var(--studio-text))', fontWeight: 600 }}>{m[1]}</strong>
      : seg
  })
}

export function PickSheet({ active, stats, history = [], onClose, openTicker, initialReceipts = false }) {
  const [shown, setShown] = useState(false)
  const [tab, setTab] = useState('receipts') // track-record section: 'receipts' | 'board'
  const receiptsRef = useRef(null)
  useEffect(() => { const a = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(a) }, [])
  // Opened via the card's "RECEIPTS →" → scroll the track-record section into
  // view once the sheet has slid up (it lives at the bottom of the scroll body).
  useEffect(() => {
    if (!initialReceipts || history.length === 0) return
    const t = setTimeout(() => receiptsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 380)
    return () => clearTimeout(t)
  }, [initialReceipts, history.length])
  if (!active) return null

  const { ticker, name } = active
  // Strip a leading heading/blockquote/bullet marker only — a bullet `*`/`-`
  // must be followed by whitespace to count, so a `**bold**` lead-in survives
  // (the old `^[#>*\-\s]+` ate the opening `**` and orphaned the closing one).
  const paras = (active.analysis_md || '').split(/\n{2,}/).map((p) => p.trim().replace(/^(?:#+\s*|>\s*|[-*]\s+)/, '')).filter(Boolean)
  const chips = Array.isArray(active.signal_chips) ? active.signal_chips : []
  const checkIns = Array.isArray(active.check_ins) ? active.check_ins : []
  const curColor = `hsl(var(${intent((active.current_pct ?? 0) >= 0)}))`
  const finColor = `hsl(var(${intent((active.final_pct ?? 0) >= 0)}))`
  // api/pick-of-week emits { count, avg_pct, hit_rate } — no wins/losses
  const picksCount = stats?.count, hitRate = stats?.hit_rate, avg = stats?.avg_pct ?? stats?.avgPct
  // "The Field" — the week's scored candidate pool (populated from the next pick
  // forward; older picks have none and the section self-hides).
  const board = Array.isArray(active.candidate_board) ? active.candidate_board : []

  // Portal to <body>. PickOfWeek renders this from INSIDE the Overview scroll
  // container (.atlas-scroll), whose -webkit-overflow-scrolling:touch traps +
  // clips position:fixed children on iOS — which pinned the sheet inside the
  // scroll area so its lower rows hid behind the ticker tape + bottom nav and
  // couldn't be scrolled into view. Rendering at <body> escapes that entirely.
  const sheet = (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'hsl(var(--studio-overlay) / 0.66)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', opacity: shown ? 1 : 0, transition: 'opacity 220ms ease' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '88%', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', borderTop: '2px solid hsl(var(--studio-accent) / 0.55)', borderRadius: '16px 16px 0 0',
        boxShadow: '0 -16px 44px hsl(var(--studio-overlay) / 0.6)',
        transform: shown ? 'translateY(0)' : 'translateY(100%)', transition: 'transform 340ms var(--studio-ease)', overflow: 'hidden' }}>
        <div style={{ padding: '9px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
            <span onClick={onClose} style={{ width: 38, height: 4, borderRadius: 999, background: 'hsl(var(--studio-border))', cursor: 'pointer' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: 'hsl(var(--studio-accent))', animation: REDUCE ? 'none' : 'atlasLive 1.6s ease-in-out infinite' }} />
              <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))' }}>PICK OF THE WEEK · LIVE</span>
            </span>
            <button onClick={onClose} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 'var(--studio-radius-sm)', cursor: 'pointer', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', color: 'hsl(var(--studio-text-muted))', fontSize: 14 }}>✕</button>
          </div>
        </div>

        <div className="atlas-scroll" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 30px' }}>
          {/* ticker header */}
          <div onClick={() => openTicker?.(ticker)} style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 16, cursor: 'pointer' }}>
            <StockLogo ticker={ticker} size={38} rounded="50%" />
            <div>
              <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 20, color: 'hsl(var(--studio-text))', letterSpacing: '0.02em' }}>{ticker}</span>
              {name && <div style={{ fontSize: 12, color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>{name}</div>}
            </div>
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--studio-font-mono)', fontSize: 9, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))' }}>CHART →</span>
          </div>

          {/* price strip */}
          <div style={{ display: 'flex', border: '1px solid hsl(var(--studio-border) / 0.7)', borderRadius: 'var(--studio-radius)', overflow: 'hidden', marginBottom: 18, background: 'hsl(var(--studio-surface) / 0.5)' }}>
            <PriceCell label="LOCKED" value={fmtPrice(active.locked_price)} sub={active.locked_at ? new Date(active.locked_at).toLocaleDateString() : null} />
            <PriceCell label="CURRENT" value={fmtPrice(active.current_price)} sub={fmtPct(active.current_pct)} subColor={curColor} divider />
            <PriceCell label="FINAL" value={fmtPrice(active.final_price)} sub={fmtPct(active.final_pct)} subColor={finColor} divider />
          </div>

          {/* thesis */}
          {active.thesis && (
            <p style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: 16, lineHeight: 1.5, color: 'hsl(var(--studio-text))', margin: '0 0 14px', textWrap: 'pretty' }}>{active.thesis}</p>
          )}

          {/* full analysis */}
          {paras.length > 0 ? paras.map((p, i) => (
            <p key={i} style={{ fontSize: 13.5, lineHeight: 1.7, color: 'hsl(var(--studio-text-muted))', margin: i === 0 ? '0 0 12px' : '12px 0 0' }}>{renderInline(p)}</p>
          )) : (
            <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 10, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', padding: '10px 12px', border: '1px dashed hsl(var(--studio-border))', borderRadius: 6 }}>FULL ANALYSIS GENERATING — REFRESH SHORTLY</div>
          )}

          {/* 8/8 gate */}
          {active.gate_level === '8of8' && (
            <div style={{ marginTop: 16 }}>
              <span style={{ display: 'inline-block', padding: '5px 10px', borderRadius: 4, background: 'hsl(var(--studio-success) / 0.1)', border: '1px solid hsl(var(--studio-success) / 0.3)', fontFamily: 'var(--studio-font-mono)', fontSize: 9, letterSpacing: '0.1em', color: 'hsl(var(--studio-success))' }}>PASSED 8/8 SETUP CHECKS · MINERVINI</span>
            </div>
          )}

          {/* signal chips */}
          {chips.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.16em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 8 }}>SIGNALS</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {chips.map((c, i) => {
                  const text = typeof c === 'string' ? c : (c.label || c.text || c.type || '')
                  return <span key={i} style={{ padding: '4px 9px', borderRadius: 4, background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', fontFamily: 'var(--studio-font-mono)', fontSize: 10, color: 'hsl(var(--studio-text-muted))' }}>{text}</span>
                })}
              </div>
            </div>
          )}

          {/* check-ins */}
          {checkIns.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.16em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 8 }}>CHECK-INS</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {checkIns.map((c, i) => (
                  <div key={i} style={{ padding: '9px 11px', borderRadius: 6, background: 'hsl(var(--studio-surface) / 0.5)', border: '1px solid hsl(var(--studio-border) / 0.6)', fontSize: 12, color: 'hsl(var(--studio-text-muted))', lineHeight: 1.5 }}>
                    {c.date && <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9, color: 'hsl(var(--studio-text-subtle))', marginBottom: 3 }}>{c.date}</div>}
                    {c.note || c.text || ''}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Track record — one section with a Receipts / Board switch (Ben: not
              two separate sections). Receipts = closed-pick history; The Board =
              this week's scored candidate field (empty for picks locked before the
              feature shipped → its tab shows a "fills in next pick" note). */}
          {(history.length > 0 || board.length > 0 || wins != null) && (
            <div ref={receiptsRef} style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', scrollMarginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 13 }}>
                <div style={{ display: 'flex', gap: 15 }}>
                  {[['receipts', `RECEIPTS${history.length ? ` (${history.length})` : ''}`], ['board', 'THE BOARD']].map(([id, label]) => (
                    <button key={id} onClick={() => setTab(id)} style={{ background: 'none', border: 'none', padding: '0 0 4px', cursor: 'pointer',
                      fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.08em',
                      color: tab === id ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text-subtle))',
                      borderBottom: tab === id ? '1.5px solid hsl(var(--studio-accent))' : '1.5px solid transparent', transition: 'color 150ms' }}>{label}</button>
                  ))}
                </div>
                {picksCount > 0 && (
                  <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', flexShrink: 0 }}>
                    <span style={{ color: 'hsl(var(--studio-text))' }}>{picksCount} PICKS</span>
                    {hitRate != null && <> · <span style={{ color: `hsl(var(${hitRate >= 0.5 ? '--studio-success' : '--studio-danger'}))` }}>{Math.round(hitRate * 100)}%</span></>}
                    {avg != null && <> · <span style={{ color: `hsl(var(${avg >= 0 ? '--studio-success' : '--studio-danger'}))` }}>{avg >= 0 ? '+' : ''}{Number(avg).toFixed(1)}%</span></>}
                  </span>
                )}
              </div>

              {tab === 'receipts' ? (
                history.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {history.map((p) => {
                      const pct = p.final_pct == null ? null : Number(p.final_pct)
                      const col = pct == null ? 'hsl(var(--studio-text-subtle))' : `hsl(var(${pct >= 0 ? '--studio-success' : '--studio-danger'}))`
                      const wk = p.week_start ? new Date(p.week_start + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''
                      return (
                        <div key={p.id ?? `${p.ticker}-${p.week_start}`} onClick={() => openTicker?.(p.ticker)} role="button"
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, background: 'hsl(var(--studio-surface) / 0.5)', border: '1px solid hsl(var(--studio-border) / 0.6)', cursor: 'pointer' }}>
                          <StockLogo ticker={p.ticker} size={22} rounded="50%" />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 12.5, color: 'hsl(var(--studio-text))' }}>{p.ticker}</span>
                            {wk && <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, color: 'hsl(var(--studio-text-subtle))', marginLeft: 7 }}>wk {wk}</span>}
                            {p.name && <div style={{ fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>}
                          </div>
                          <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 13, color: col, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                            {pct == null ? '—' : `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div style={{ fontFamily: 'var(--studio-font-sans)', fontSize: 12, lineHeight: 1.55, color: 'hsl(var(--studio-text-subtle))', padding: '2px 0' }}>No closed picks yet — the track record builds as picks close out.</div>
                )
              ) : (
                board.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {board.map((c, i) => {
                      const isPick = !!c.is_pick
                      const score = c.setup_score == null ? null : Math.round(Number(c.setup_score))
                      const ext = c.extension_atr == null ? null : Number(c.extension_atr)
                      return (
                        <div key={c.ticker ?? i} onClick={() => openTicker?.(c.ticker)} role="button"
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6,
                            background: isPick ? 'hsl(var(--studio-accent) / 0.08)' : 'hsl(var(--studio-surface) / 0.5)',
                            border: `1px solid ${isPick ? 'hsl(var(--studio-accent) / 0.45)' : 'hsl(var(--studio-border) / 0.6)'}`, cursor: 'pointer' }}>
                          <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 10, color: 'hsl(var(--studio-text-subtle))', width: 16, flexShrink: 0, textAlign: 'right' }}>{i + 1}</span>
                          <StockLogo ticker={c.ticker} size={22} rounded="50%" />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 12.5, color: 'hsl(var(--studio-text))' }}>{c.ticker}</span>
                              {isPick && <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 8, letterSpacing: '0.12em', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.5)', borderRadius: 3, padding: '1px 4px' }}>PICK</span>}
                            </span>
                            {(c.reason || c.name) && (
                              <div style={{ fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>
                                {isPick ? (c.name || 'the pick') : (c.reason || c.name)}
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 13, color: isPick ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text))', fontVariantNumeric: 'tabular-nums' }}>{score == null ? '—' : score}</div>
                            {ext != null && <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 1 }}>{ext.toFixed(1)} ATR</div>}
                          </div>
                        </div>
                      )
                    })}
                    <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', marginTop: 2, lineHeight: 1.5 }}>
                      SETUP SCORE 0–100 · EVERY NAME CLEARED THE 8/8 TREND GATE · THE PICK ISN’T ALWAYS THE TOP SCORE — ENTRY QUALITY &amp; RISK MATTER TOO
                    </div>
                  </div>
                ) : (
                  <div style={{ fontFamily: 'var(--studio-font-sans)', fontSize: 12, lineHeight: 1.55, color: 'hsl(var(--studio-text-subtle))', padding: '2px 0' }}>Fills in with this week’s pick — this one was locked before the board existed.</div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
  return typeof document !== 'undefined' ? createPortal(sheet, document.body) : sheet
}
